import { useId, useRef, useState } from 'react'
import { Paperclip } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { ComposerFileCard } from './ai-chat-file-card'
import { MAX_FILES_PER_MESSAGE } from './chat-file-format'
import type { DragEvent, ReactNode } from 'react'
import type { ComposerFile } from './use-ai-chat-files'
import { Button } from '@/shared/ui/button'
import { cn } from '@/shared/lib/utils'

export function AiChatFileTray({
  files,
  notice,
  blocked,
  onRemove,
  onRetry,
}: {
  files: Array<ComposerFile>
  notice: 'tooMany' | null
  blocked: boolean
  onRemove: (key: string) => void
  onRetry: (key: string) => void
}) {
  const { t } = useTranslation()
  if (!files.length && !notice) return null

  return (
    <div className="mt-2 space-y-2">
      {files.length ? (
        <ul aria-label={t('aiChat:files.tray')} className="space-y-2">
          {files.map((file) => (
            <ComposerFileCard
              key={file.key}
              file={file}
              onRemove={onRemove}
              onRetry={onRetry}
            />
          ))}
        </ul>
      ) : null}
      {blocked && files.length ? (
        <p role="status" className="text-xs text-muted-foreground">
          {t('aiChat:files.sendBlocked')}
        </p>
      ) : null}
      {notice ? (
        <p role="alert" className="text-xs text-destructive">
          {t('aiChat:files.tooMany', { count: MAX_FILES_PER_MESSAGE })}
        </p>
      ) : null}
    </div>
  )
}

export function AiChatAttachButton({
  disabled,
  onFiles,
}: {
  disabled: boolean
  onFiles: (files: Array<File>) => void
}) {
  const { t } = useTranslation()
  const inputId = useId()
  const input = useRef<HTMLInputElement>(null)

  return (
    <>
      <input
        id={inputId}
        ref={input}
        type="file"
        multiple
        hidden
        data-testid="ai-chat-file-input"
        aria-label={t('aiChat:files.choose')}
        onChange={(event) => {
          const chosen = Array.from(event.target.files ?? [])
          event.target.value = ''
          if (chosen.length) onFiles(chosen)
        }}
      />
      <Button
        type="button"
        variant="outline"
        size="sm"
        disabled={disabled}
        onClick={() => input.current?.click()}
      >
        <Paperclip aria-hidden="true" />
        {t('aiChat:files.attach')}
      </Button>
    </>
  )
}

/** Drag-and-drop region around the composer with a visible drop hint. */
export function AiChatDropRegion({
  children,
  disabled,
  onFiles,
}: {
  children: ReactNode
  disabled: boolean
  onFiles: (files: Array<File>) => void
}) {
  const { t } = useTranslation()
  const [active, setActive] = useState(false)
  const hasFiles = (event: DragEvent) =>
    Array.from(event.dataTransfer.types).includes('Files')

  return (
    <div
      className={cn('relative', active && 'outline-2 outline-primary')}
      onDragEnter={(event) => {
        if (disabled || !hasFiles(event)) return
        event.preventDefault()
        setActive(true)
      }}
      onDragOver={(event) => {
        if (disabled || !hasFiles(event)) return
        event.preventDefault()
        setActive(true)
      }}
      onDragLeave={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null))
          setActive(false)
      }}
      onDrop={(event) => {
        setActive(false)
        if (disabled || !hasFiles(event)) return
        event.preventDefault()
        const dropped = Array.from(event.dataTransfer.files)
        if (dropped.length) onFiles(dropped)
      }}
    >
      {children}
      {active ? (
        <div
          role="status"
          className="pointer-events-none absolute inset-0 flex items-center justify-center rounded-md bg-background/90 text-sm font-medium"
        >
          {t('aiChat:files.dropHint')}
        </div>
      ) : null}
    </div>
  )
}
